import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Car, Menu, X, Phone, Mail, MapPin } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function Header({ variant = "light" }) {
  const location = useLocation();
  const [siteSettings, setSiteSettings] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchSiteSettings();
  }, []);

  const fetchSiteSettings = async () => {
    try {
      const response = await axios.get(`${API}/cms/site-settings`);
      setSiteSettings(response.data.settings);
    } catch (error) {
      console.error("Failed to fetch site settings:", error);
      // Use defaults
      setSiteSettings({
        logo_url: "",
        logo_text: "Roman Cars",
        contact: {
          phone: "+44 123 456 7890",
          email: "info@romancars.uk",
          address: "London, United Kingdom"
        },
        menu_items: [
          { id: "1", label: "Home", url: "/", order: 0 },
          { id: "2", label: "Book Now", url: "/book", order: 1 },
          { id: "3", label: "About Us", url: "/about", order: 2 },
          { id: "4", label: "Contact Us", url: "/contact", order: 3 },
          { id: "5", label: "Services", url: "/services", order: 4 }
        ]
      });
    }
  };

  const isActive = (url) => {
    if (url === "/") return location.pathname === "/";
    return location.pathname.startsWith(url);
  };

  const sortedMenuItems = siteSettings?.menu_items?.sort((a, b) => a.order - b.order) || [];
  const contact = siteSettings?.contact || {};

  return (
    <header className="w-full">
      {/* Top Bar with Contact Info */}
      <div className="bg-gray-900 text-white py-2">
        <div className="max-w-7xl mx-auto px-4 flex flex-wrap justify-between items-center text-sm">
          <div className="flex items-center gap-6">
            {contact.phone && (
              <a href={`tel:${contact.phone}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors">
                <Phone size={14} />
                <span>{contact.phone}</span>
              </a>
            )}
            {contact.email && (
              <a href={`mailto:${contact.email}`} className="flex items-center gap-2 hover:text-orange-400 transition-colors">
                <Mail size={14} />
                <span>{contact.email}</span>
              </a>
            )}
          </div>
          {contact.address && (
            <div className="flex items-center gap-2 text-gray-300">
              <MapPin size={14} />
              <span>{contact.address}</span>
            </div>
          )}
        </div>
      </div>

      {/* Main Header */}
      <div className="bg-white shadow-sm px-4 md:px-6 py-4">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3">
            {siteSettings?.logo_url ? (
              <img 
                src={siteSettings.logo_url.startsWith("http") ? siteSettings.logo_url : `${process.env.REACT_APP_BACKEND_URL}${siteSettings.logo_url}`}
                alt={siteSettings.logo_text || "Logo"} 
                className="h-12 md:h-16 w-auto object-contain"
              />
            ) : (
              <div className="w-12 h-12 bg-orange-500 rounded-xl flex items-center justify-center">
                <Car size={28} className="text-white" />
              </div>
            )}
            <span className="text-xl md:text-2xl font-bold text-gray-900">
              {siteSettings?.logo_text || "Roman Cars"}
            </span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden md:flex items-center gap-1">
            {sortedMenuItems.map((item) => {
              const isBookNow = item.label.toLowerCase().includes("book");
              if (isBookNow) {
                return (
                  <Link
                    key={item.id}
                    to={item.url}
                    className="px-5 py-2.5 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium ml-2"
                  >
                    {item.label}
                  </Link>
                );
              }
              return (
                <Link
                  key={item.id}
                  to={item.url}
                  className={`px-4 py-2 rounded-lg transition-colors ${
                    isActive(item.url) 
                      ? "text-orange-500 font-semibold" 
                      : "text-gray-700 hover:text-orange-500 hover:bg-gray-50"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>

          {/* Mobile Menu Button */}
          <button
            className="md:hidden text-gray-700 p-2"
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Navigation */}
      {mobileMenuOpen && (
        <div className="md:hidden bg-white border-t shadow-lg">
          <nav className="flex flex-col p-4 space-y-2">
            {sortedMenuItems.map((item) => {
              const isBookNow = item.label.toLowerCase().includes("book");
              if (isBookNow) {
                return (
                  <Link
                    key={item.id}
                    to={item.url}
                    onClick={() => setMobileMenuOpen(false)}
                    className="px-4 py-3 bg-orange-500 text-white rounded-lg hover:bg-orange-600 transition-colors font-medium text-center"
                  >
                    {item.label}
                  </Link>
                );
              }
              return (
                <Link
                  key={item.id}
                  to={item.url}
                  onClick={() => setMobileMenuOpen(false)}
                  className={`px-4 py-3 rounded-lg transition-colors ${
                    isActive(item.url) 
                      ? "text-orange-500 font-semibold bg-orange-50" 
                      : "text-gray-700 hover:text-orange-500 hover:bg-gray-50"
                  }`}
                >
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </div>
      )}
    </header>
  );
}
